import React, { useState, useEffect, useCallback, useRef } from 'react';
import CourseCard from './CourseCard';
import { CourseCardShimmer } from '../common/Shimmer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Course } from '../../hooks/useCourseApi';
import axios from 'axios';
import { getTenantHeaders } from '../../config/tenant';

interface FoundationCourseResponse {
  success: boolean;
  message: string;
  data: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    courses: Course[];
  };
}

const FoundationCourseList: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const animationRef = useScrollAnimation();

  const fetchFoundationCourses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...getTenantHeaders()
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get<FoundationCourseResponse>(
        'https://api.ibrahim-magdy.com/api/AdminStudent/CourseActive',
        {
          params: { grade: 'Generic' },
          headers
        }
      );

      if (response.data.success) {
        setCourses(response.data.data.courses);
      } else {
        setError(response.data.message || 'Failed to fetch foundation courses');
      }
    } catch (err: any) {
      console.error('Error fetching foundation courses:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch foundation courses');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFoundationCourses();
  }, [fetchFoundationCourses]);

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        Error loading foundation courses: {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto px-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <CourseCardShimmer key={`foundation-shimmer-${index}`} />
        ))}
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block p-4 bg-primary-light rounded-full mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد كورسات تأسيس متاحة</h3>
        <p className="text-gray-500">لا توجد كورسات تأسيس متاحة حالياً. يرجى المحاولة مرة أخرى لاحقاً.</p>
      </div>
    );
  }

  return (
    <div 
      ref={animationRef}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto px-4"
    >
      {courses.map((course) => (
        <CourseCard key={course.id} course={course} />
      ))}
    </div>
  );
};

export default FoundationCourseList; 